import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Save, Upload, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/media")({
  head: () => ({
    meta: [
      { title: "Admin — Foto's beheren" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminMediaPage,
});

type Row = {
  key: string;
  desktop_path: string | null;
  mobile_path: string | null;
  alt: string | null;
  credit: string | null;
  overlay_opacity: number;
  glow_opacity: number;
};

const SLOTS: { key: string; label: string; hint: string; overlay?: boolean }[] = [
  {
    key: "hero",
    label: "Hero (bovenaan homepage)",
    hint: "Grote achtergrondfoto. Liggend, minimaal 1920×1280.",
    overlay: true,
  },
  { key: "gallery_screen", label: "Sfeer — groot doek", hint: "Brede banner bovenaan het sfeerblok (liggend, bijv. 21:9)." },
  { key: "gallery_audience", label: "Sfeer — publiek", hint: "Staand beeld (4:5)." },
  { key: "gallery_horeca", label: "Sfeer — horeca", hint: "Staand beeld (4:5)." },
  { key: "gallery_couple", label: "Sfeer — bezoekers", hint: "Liggend beeld (4:3)." },
  { key: "gallery_drinks", label: "Sfeer — drankjes", hint: "Liggend beeld (4:3)." },
];

const ACCEPT = "image/jpeg,image/png,image/webp,image/avif";
const MAX_BYTES = 8 * 1024 * 1024;

function AdminMediaPage() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Record<string, Row> | null>(null);
  const [previews, setPreviews] = useState<Record<string, { desktop: string | null; mobile: string | null }>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  async function load() {
    const { data, error } = await supabase
      .from("page_media")
      .select("key, desktop_path, mobile_path, alt, credit, overlay_opacity, glow_opacity");
    if (error) return toast.error(error.message);
    const map: Record<string, Row> = {};
    for (const s of SLOTS) {
      const found = (data ?? []).find((d) => d.key === s.key);
      map[s.key] = {
        key: s.key,
        desktop_path: found?.desktop_path ?? null,
        mobile_path: found?.mobile_path ?? null,
        alt: found?.alt ?? null,
        credit: (found as { credit?: string | null } | undefined)?.credit ?? null,
        overlay_opacity: Number(found?.overlay_opacity ?? 1),
        glow_opacity: Number(found?.glow_opacity ?? 1),
      };
    }
    setRows(map);

    const sign = async (p: string | null) => {
      if (!p) return null;
      const { data } = await supabase.storage.from("page-media").createSignedUrl(p, 60 * 60);
      return data?.signedUrl ?? null;
    };
    const entries = await Promise.all(
      SLOTS.map(async (s) => [
        s.key,
        { desktop: await sign(map[s.key].desktop_path), mobile: await sign(map[s.key].mobile_path) },
      ] as const),
    );
    setPreviews(Object.fromEntries(entries));
  }

  useEffect(() => {
    load();
  }, []);

  function patchLocal(key: string, patch: Partial<Row>) {
    setRows((prev) => (prev ? { ...prev, [key]: { ...prev[key], ...patch } } : prev));
  }

  async function persist(key: string, patch: Partial<Row>) {
    const { error } = await supabase
      .from("page_media")
      .upsert({ key, ...patch }, { onConflict: "key" });
    if (error) {
      toast.error(error.message);
      return false;
    }
    qc.invalidateQueries({ queryKey: ["page-media"] });
    return true;
  }

  async function uploadFile(key: string, variant: "desktop" | "mobile", file: File) {
    if (file.size > MAX_BYTES) return toast.error("Bestand te groot (max 8 MB).");
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${key}/${variant}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const up = await supabase.storage.from("page-media").upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
    if (up.error) return toast.error("Upload mislukt: " + up.error.message);
    const ok = await persist(key, variant === "desktop" ? { desktop_path: path } : { mobile_path: path });
    if (ok) {
      toast.success("Afbeelding geüpload");
      await load();
    }
  }

  async function removeFile(key: string, variant: "desktop" | "mobile") {
    const ok = await persist(key, variant === "desktop" ? { desktop_path: null } : { mobile_path: null });
    if (ok) {
      toast.success("Verwijderd — standaardafbeelding actief");
      await load();
    }
  }

  async function saveSlot(key: string) {
    if (!rows) return;
    setSavingKey(key);
    const r = rows[key];
    const ok = await persist(key, {
      alt: r.alt?.trim() ? r.alt.trim() : null,
      credit: r.credit?.trim() ? r.credit.trim() : null,
      overlay_opacity: r.overlay_opacity,
      glow_opacity: r.glow_opacity,
    });
    setSavingKey(null);
    if (ok) toast.success("Opgeslagen");
  }

  if (!rows) {
    return <div className="p-10 text-foreground/70">Laden…</div>;
  }

  return (
    <div className="px-5 py-10 md:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <p className="font-mono-cinema text-[10px] text-gold">— Admin</p>
          <h1 className="mt-2 font-display text-3xl font-medium md:text-4xl">Foto's beheren</h1>
          <p className="mt-2 text-sm text-foreground/70">
            Vervang de hero-foto en de sfeerfoto's, stel alt-teksten in en regel de donkere overlay
            over de hero. Geen upload = de standaardafbeelding blijft staan.
          </p>
        </div>

        <div className="space-y-6">
          {SLOTS.map((slot) => {
            const r = rows[slot.key];
            const p = previews[slot.key] ?? { desktop: null, mobile: null };
            return (
              <section
                key={slot.key}
                className="rounded-2xl border border-border bg-night-deep/50 p-6 md:p-8"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-xl">{slot.label}</h2>
                    <p className="mt-1 text-sm text-foreground/65">{slot.hint}</p>
                  </div>
                  <Button
                    variant="hero"
                    size="sm"
                    onClick={() => saveSlot(slot.key)}
                    disabled={savingKey === slot.key}
                  >
                    <Save className="h-4 w-4" /> {savingKey === slot.key ? "Bezig…" : "Opslaan"}
                  </Button>
                </div>

                <div className="mt-6 grid gap-5 md:grid-cols-2">
                  <ImageSlot
                    label="Desktop / standaard"
                    preview={p.desktop}
                    currentPath={r.desktop_path}
                    onFile={(f) => uploadFile(slot.key, "desktop", f)}
                    onRemove={() => removeFile(slot.key, "desktop")}
                  />
                  <ImageSlot
                    label="Mobiel (optioneel)"
                    preview={p.mobile}
                    currentPath={r.mobile_path}
                    onFile={(f) => uploadFile(slot.key, "mobile", f)}
                    onRemove={() => removeFile(slot.key, "mobile")}
                  />
                </div>

                <div className="mt-5">
                  <Label className="text-sm font-medium">Alt-tekst (voor SEO &amp; screenreaders)</Label>
                  <Input
                    value={r.alt ?? ""}
                    placeholder="Bijvoorbeeld: publiek kijkt naar film onder de sterren"
                    onChange={(e) => patchLocal(slot.key, { alt: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div className="mt-5">
                  <Label className="text-sm font-medium">Fotocredit (naam fotograaf)</Label>
                  <Input
                    value={r.credit ?? ""}
                    placeholder="Bijvoorbeeld: Jan Jansen"
                    onChange={(e) => patchLocal(slot.key, { credit: e.target.value })}
                    className="mt-2"
                  />
                  <p className="mt-1.5 text-xs text-foreground/60">
                    Wordt zichtbaar bij de foto op de website (leeg = geen credit).
                  </p>
                </div>

                {slot.overlay && (
                  <div className="mt-6 grid gap-6 rounded-xl border border-border/70 bg-night/40 p-4 md:grid-cols-2">
                    <OpacityControl
                      label="Donkere overlay"
                      help="Hoger = donkerder beeld, tekst beter leesbaar."
                      value={r.overlay_opacity}
                      onChange={(v) => patchLocal(slot.key, { overlay_opacity: v })}
                    />
                    <OpacityControl
                      label="Gouden gloed"
                      help="Warme lichtgloed onderaan de hero."
                      value={r.glow_opacity}
                      onChange={(v) => patchLocal(slot.key, { glow_opacity: v })}
                    />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
      <Toaster position="top-center" />
    </div>
  );
}

function OpacityControl({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <span className="font-mono-cinema text-[11px] text-gold">{Math.round(value * 100)}%</span>
      </div>
      <p className="mt-0.5 text-xs text-foreground/60">{help}</p>
      <Slider
        className="mt-4"
        min={0}
        max={100}
        step={5}
        value={[Math.round(value * 100)]}
        onValueChange={(v) => onChange((v[0] ?? 100) / 100)}
      />
    </div>
  );
}

function ImageSlot({
  label,
  preview,
  currentPath,
  onFile,
  onRemove,
}: {
  label: string;
  preview: string | null;
  currentPath: string | null;
  onFile: (f: File) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-xl border border-border/70 bg-night/40 p-4">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="mt-3 flex h-40 items-center justify-center overflow-hidden rounded-lg bg-night-deep">
        {preview ? (
          <img src={preview} alt={label} className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs text-foreground/50">Standaardafbeelding actief</span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-secondary">
          <Upload className="h-4 w-4" />
          <span>{currentPath ? "Vervangen" : "Uploaden"}</span>
          <input
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.currentTarget.value = "";
            }}
          />
        </label>
        {currentPath && (
          <Button variant="ghostLine" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4" /> Verwijderen
          </Button>
        )}
      </div>
    </div>
  );
}
